<template>
  <transition
    enter-active-class="tw-transition-all tw-duration-300 tw-ease-out"
    enter-from-class="tw-transform tw-translate-y-[-100%] tw-opacity-0"
    enter-to-class="tw-transform tw-translate-y-0 tw-opacity-100"
    leave-active-class="tw-transition-all tw-duration-300 tw-ease-in"
    leave-from-class="tw-transform tw-translate-y-0 tw-opacity-100"
    leave-to-class="tw-transform tw-translate-y-[-100%] tw-opacity-0"
  >
    <div 
      v-if="hasUnreadOrderReminders"
      class="tw-bg-amber-50 tw-border-b tw-border-amber-200 tw-px-4 tw-py-2 tw-relative tw-z-10"
      role="alert"
      aria-live="polite"
      aria-label="Order reminder alert"
    >
      <div class="tw-max-w-7xl tw-mx-auto">
        <!-- Header -->
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <div class="tw-flex tw-items-center tw-gap-2">
            <icon icon="solar:bell-bold" class="tw-text-amber-600 tw-text-lg" />
            <h3 class="tw-text-sm tw-font-semibold tw-text-amber-800">
              {{ t('notifications.order_reminder_banner.title') }}
            </h3>
            <span class="tw-bg-amber-200 tw-text-amber-800 tw-text-xs tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full">
              {{ unreadOrderReminders.length }}
            </span>
          </div>
          
          <div class="tw-flex tw-items-center tw-gap-2">
            <!-- Expand/Collapse Button -->
            <button
              @click="toggleExpanded"
              class="tw-text-amber-600 hover:tw-text-amber-700 tw-text-xs tw-font-medium hover:tw-bg-amber-100 tw-px-2 tw-py-1 tw-rounded tw-transition-colors tw-duration-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-amber-500 focus:tw-ring-offset-2"
              :aria-label="isExpanded ? t('notifications.order_reminder_banner.collapse') : t('notifications.order_reminder_banner.expand')"
            >
              <div class="tw-flex tw-items-center tw-gap-1">
                <span>{{ isExpanded ? t('notifications.order_reminder_banner.collapse') : t('notifications.order_reminder_banner.expand') }}</span>
                <icon 
                  :icon="isExpanded ? 'solar:alt-arrow-up-line-duotone' : 'solar:alt-arrow-down-line-duotone'" 
                  class="tw-text-xs tw-transition-transform tw-duration-200"
                />
              </div>
            </button>
            
            <!-- Dismiss All Button -->
            <button
              @click="handleDismissAll"
              class="tw-text-amber-600 hover:tw-text-amber-700 tw-text-xs tw-font-medium hover:tw-bg-amber-100 tw-px-2 tw-py-1 tw-rounded tw-transition-colors tw-duration-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-amber-500 focus:tw-ring-offset-2"
              :aria-label="t('notifications.order_reminder_banner.dismiss_all')"
            >
              {{ t('notifications.order_reminder_banner.dismiss_all') }}
            </button>
          </div>
        </div>
        
        <!-- Expanded Content -->
        <transition
          enter-active-class="tw-transition-all tw-duration-300 tw-ease-out"
          enter-from-class="tw-max-h-0 tw-opacity-0"
          enter-to-class="tw-max-h-[2000px] tw-opacity-100"
          leave-active-class="tw-transition-all tw-duration-300 tw-ease-in"
          leave-from-class="tw-max-h-[2000px] tw-opacity-100"
          leave-to-class="tw-max-h-0 tw-opacity-0"
        >
          <div v-if="isExpanded" class="tw-overflow-hidden">
            <!-- Controls Section -->
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-3 tw-px-1">
              <!-- Per Page Selector -->
              <div class="tw-flex tw-items-center tw-gap-2">
                <label class="tw-text-xs tw-text-amber-700 tw-font-medium">
                  {{ t('notifications.order_reminder_banner.per_page') }}:
                </label>
                <select 
                  v-model="itemsPerPage"
                  @change="onPerPageChange"
                  class="tw-text-xs tw-bg-white tw-border tw-border-amber-200 tw-rounded tw-px-2 tw-py-1 tw-text-amber-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-amber-500 focus:tw-border-amber-500"
                >
                  <option :value="5">5</option>
                  <option :value="10">10</option>
                  <option :value="15">15</option>
                  <option :value="20">20</option>
                </select>
              </div>
              
              <!-- Page Info -->
              <div class="tw-text-xs tw-text-amber-700" v-if="totalPages > 1">
                {{ t('notifications.order_reminder_banner.showing_page', { current: currentPage, total: totalPages }) }}
              </div>
            </div>
            
            <!-- Order Rows -->
            <div class="tw-space-y-1 tw-mb-3">
              <div 
                v-for="notification in paginatedReminders" 
                :key="notification.id"
                class="tw-flex tw-items-center tw-justify-between tw-bg-white tw-rounded-lg tw-px-3 tw-py-2 tw-border tw-border-amber-200 hover:tw-border-amber-300 tw-transition-colors tw-duration-200"
              >
                <!-- Order Info -->
                <div class="tw-flex tw-items-center tw-gap-3 tw-flex-1 tw-min-w-0">
                  <!-- Order Icon -->
                  <div class="tw-w-6 tw-h-6 tw-rounded-full tw-bg-amber-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                    <icon icon="solar:box-bold" class="tw-text-amber-600 tw-text-xs" />
                  </div>
                  
                  <!-- Order Details -->
                  <div class="tw-flex-1 tw-min-w-0">
                    <div class="tw-flex tw-items-center tw-gap-2 tw-mb-0.5">
                      <span class="tw-text-sm tw-font-medium tw-text-gray-900">
                        #{{ notification.data?.order?.id || 'N/A' }}
                      </span>
                      <span class="tw-text-xs tw-text-gray-500">
                        {{ notification.data?.order?.customer_name || 'Unknown Customer' }}
                      </span>
                    </div>
                    <p class="tw-text-xs tw-text-gray-600 tw-line-clamp-1">
                      {{ notification.message || t('notifications.order_reminder_banner.needs_followup') }}
                    </p>
                  </div>
                </div>
                
                <!-- Actions -->
                <div class="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
                  <!-- View Order Button -->
                  <button
                    @click="handleViewOrder(notification)"
                    class="tw-bg-amber-600 hover:tw-bg-amber-700 tw-text-white tw-text-xs tw-font-medium tw-px-3 tw-py-1.5 tw-rounded tw-transition-colors tw-duration-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-amber-500 focus:tw-ring-offset-2"
                    :aria-label="t('notifications.order_reminder_banner.view_order')"
                  >
                    {{ t('notifications.order_reminder_banner.view_order') }}
                  </button>
                  
                  <!-- Dismiss Single Button -->
                  <button
                    @click="handleDismissSingle(notification.id)"
                    class="tw-text-amber-600 hover:tw-text-amber-700 tw-p-1 tw-rounded hover:tw-bg-amber-100 tw-transition-colors tw-duration-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-amber-500 focus:tw-ring-offset-2"
                    :aria-label="t('notifications.order_reminder_banner.dismiss')"
                  >
                    <icon icon="solar:close-circle-bold" class="tw-text-sm" />
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Pagination Controls -->
            <div v-if="totalPages > 1" class="tw-flex tw-items-center tw-justify-center tw-gap-2">
              <button
                @click="goToPreviousPage"
                :disabled="currentPage === 1"
                class="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-amber-700 tw-bg-white tw-border tw-border-amber-200 tw-rounded hover:tw-bg-amber-50 tw-transition-colors tw-duration-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-amber-500 focus:tw-ring-offset-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed disabled:hover:tw-bg-white"
                :aria-label="t('buttons.previous')"
              >
                <icon 
                  :icon="$i18n.locale === 'ar' ? 'solar:arrow-right-line-duotone' : 'solar:arrow-left-line-duotone'" 
                  class="tw-text-xs" 
                />
                <span>{{ t('buttons.previous') }}</span>
              </button>
              
              <button
                @click="goToNextPage"
                :disabled="currentPage === totalPages"
                class="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-amber-700 tw-bg-white tw-border tw-border-amber-200 tw-rounded hover:tw-bg-amber-50 tw-transition-colors tw-duration-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-amber-500 focus:tw-ring-offset-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed disabled:hover:tw-bg-white"
                :aria-label="t('buttons.next')"
              >
                <span>{{ t('buttons.next') }}</span>
                <icon 
                  :icon="$i18n.locale === 'ar' ? 'solar:arrow-left-line-duotone' : 'solar:arrow-right-line-duotone'" 
                  class="tw-text-xs" 
                />
              </button>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </transition>
  
  <!-- Order Update Popup -->
  <update-order-popup 
    v-if="showOrderUpdatePopup" 
    :visible="showOrderUpdatePopup" 
    :item="selectedOrder" 
    @update:visible="handleOrderUpdateVisibilityChange"
    @update="handleOrderUpdate"
  />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores';
import UpdateOrderPopup from '@/components/orders/UpdateOrderPopup.vue';

const { t } = useI18n();
const authStore = useAuthStore();

// State
const notifications = ref([]);
const notificationManager = ref(null);
const showOrderUpdatePopup = ref(false);
const selectedOrder = ref(null);
const dismissedReminders = ref(new Set());

// Expand/Collapse state
const isExpanded = ref(false);

// Pagination state
const currentPage = ref(1);
const itemsPerPage = ref(5);

// Computed properties
const unreadOrderReminders = computed(() => {
  return notifications.value.filter(notification => 
    notification.unread && 
    notification.data?.type === 'order_reminder' &&
    !dismissedReminders.value.has(notification.id)
  );
});

const hasUnreadOrderReminders = computed(() => {
  return unreadOrderReminders.value.length > 0;
});

const totalPages = computed(() => {
  return Math.ceil(unreadOrderReminders.value.length / itemsPerPage.value);
});

const paginatedReminders = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return unreadOrderReminders.value.slice(start, end);
});

// Methods
const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const goToPreviousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const goToNextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const onPerPageChange = () => {
  // Reset to first page when changing items per page
  currentPage.value = 1;
};

const handleViewOrder = (notification) => {
  // Mark the notification as read first
  if (notificationManager.value) {
    notificationManager.value.markAsRead(notification.id);
  }
  
  // Then open the order update popup
  selectedOrder.value = notification.data?.order;
  showOrderUpdatePopup.value = true;
};

const handleDismissSingle = (notificationId) => {
  dismissedReminders.value.add(notificationId);
};

const handleDismissAll = () => {
  // Mark all current unread order reminders as dismissed
  unreadOrderReminders.value.forEach(notification => {
    dismissedReminders.value.add(notification.id);
  });
};

const handleOrderUpdateVisibilityChange = (isVisible) => {
  showOrderUpdatePopup.value = isVisible;
  if (!isVisible) {
    selectedOrder.value = null;
  }
};

const handleOrderUpdate = (updatedOrder) => {
  // Handle order update if needed
  console.log('Order updated from banner:', updatedOrder);
  
  // Mark the notification as read
  if (selectedOrder.value && notificationManager.value) {
    const notification = unreadOrderReminders.value.find(n => 
      n.data?.order?.id === selectedOrder.value.id
    );
    if (notification) {
      notificationManager.value.markAsRead(notification.id);
    }
  }
};

// Initialize notification manager
const initNotificationManager = async () => {
  try {
    if (authStore.user && authStore.user.id) {
      const { notificationManager: manager } = await import('@/services/notificationManager');
      notificationManager.value = manager;
      
      // Initialize with current user ID
      await notificationManager.value.init(authStore.user.id);
      
      // Set initial notifications
      notifications.value = notificationManager.value.notifications;
      
      // Add event listeners
      notificationManager.value.addEventListener('onNotificationReceived', handleNewNotification);
      notificationManager.value.addEventListener('onNotificationUpdated', handleNotificationUpdate);
      notificationManager.value.addEventListener('onNotificationDeleted', handleNotificationDeletion);
    }
  } catch (error) {
    console.error('Failed to initialize notification manager for banner:', error);
  }
};

// Event handlers
const handleNewNotification = (notification) => {
  const existingIndex = notifications.value.findIndex(n => n.id === notification.id);
  if (existingIndex !== -1) {
    notifications.value[existingIndex] = notification;
  } else {
    notifications.value = [notification, ...notifications.value];
  }
};

const handleNotificationUpdate = (updatedNotification) => {
  const index = notifications.value.findIndex(n => n.id === updatedNotification.id);
  if (index !== -1) {
    notifications.value[index] = updatedNotification;
  }
};

const handleNotificationDeletion = (deletedNotification) => {
  notifications.value = notifications.value.filter(n => n.id !== deletedNotification.id);
  dismissedReminders.value.delete(deletedNotification.id);
};

// Watch for changes in unread order reminders to clear dismissed state and reset pagination
watch(unreadOrderReminders, (newReminders) => {
  // Clear dismissed state for notifications that are no longer in the list
  const currentIds = new Set(newReminders.map(n => n.id));
  dismissedReminders.value.forEach(id => {
    if (!currentIds.has(id)) {
      dismissedReminders.value.delete(id);
    }
  });
  
  // Reset to first page if current page is out of bounds
  if (currentPage.value > totalPages.value && totalPages.value > 0) {
    currentPage.value = 1;
  }
});

// Watch for hasUnreadOrderReminders to reset expanded state when no reminders
watch(hasUnreadOrderReminders, (hasReminders) => {
  if (!hasReminders) {
    isExpanded.value = false;
  }
});

// Lifecycle
onMounted(() => {
  initNotificationManager();
});

onUnmounted(() => {
  if (notificationManager.value) {
    notificationManager.value.removeAllEventListeners();
  }
});
</script> 